import React, { createContext, useContext, useState, useEffect } from 'react'
// import { redirectToDashboard } from '@/components/utils/authHelpers'
import { useNavigate } from 'react-router-dom'
import { User } from '@/types'
import { getApiUrl } from '@/lib/apiUrl'

type AuthUser = User & {
  firstName?: string
  lastName?: string
  isEmailVerified?: boolean
}

type AuthContextType = {
  user: AuthUser | null
  loading: boolean
  token: string | null
  login: (token: string, user: AuthUser) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  token: null,
  login: async () => {},
  logout: () => {},
  refreshUser: async () => {}
})

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'))
  const navigate = useNavigate()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const hasInitialized = React.useRef(false)

  const refreshUser = async () => {
    // Prevent concurrent refresh calls
    if (isRefreshing) {
      return
    }
    setIsRefreshing(true)
    const storedToken = localStorage.getItem('authToken')
    if (!storedToken) {
      setUser(null)
      setLoading(false)
      return
    }

    try {
      // Get API URL using helper function
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        console.error('VITE_API_URL is not set in production!');
        setUser(null);
        setLoading(false);
        return;
      }
      const response = await fetch(`${apiUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json',
        },
      })

      // Handle rate limiting (429) - don't log out user, just keep existing state
      if (response.status === 429) {
        console.warn('Rate limit reached for auth refresh. Keeping existing user session.')
        // Don't clear user or token - rate limiting is temporary
        // The user is still authenticated, just too many requests
        setLoading(false)
        setIsRefreshing(false)
        return
      }

      if (response.ok) {
        const data = await response.json()
        // Ensure user has id field (map _id to id if needed)
        const userData = data.user
        if (userData && !userData.id && userData._id) {
          userData.id = userData._id
        }
        setUser(userData)
      } else if (response.status === 401 || response.status === 403) {
        // Only log out on actual authentication errors (401 Unauthorized, 403 Forbidden)
        console.error('Auth refresh failed: Authentication error', response.status, response.statusText)
        setUser(null)
        localStorage.removeItem('authToken')
      } else {
        // For other errors (500, etc.), log but don't log out - might be temporary server issues
        console.error('Auth refresh failed:', response.status, response.statusText)
        // Keep existing user state - don't log out on server errors
      }
    } catch (err) {
      // Network errors - don't log out, might be temporary connectivity issues
      console.error('Auth refresh error:', err)
      // Keep existing user state - don't log out on network errors
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }

  const login = async (token: string, user: AuthUser) => {
    localStorage.setItem('authToken', token)
    setToken(token)
    setUser(user) // Set initial user data immediately for UI responsiveness
    setLoading(true) // Block redirect until refreshUser() completes so dashboard sees correct is_verified
    // Refresh user data to get complete information including verification status
    try {
      await refreshUser()
    } catch (error) {
      // If refresh fails, keep the initial user data from login
      console.error('Failed to refresh user data after login:', error)
    } finally {
      setLoading(false) // Allow redirect (useEffect) only after refresh is done
    }
  }

  const logout = () => {
    localStorage.removeItem('authToken')
    setUser(null)
    setToken(null)
    navigate('/auth?message=Logged out successfully')
  }

  useEffect(() => {
    // Prevent double call in React StrictMode (development)
    if (hasInitialized.current) {
      return
    }
    hasInitialized.current = true
    refreshUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, token, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
